import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, CheckCircle2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "../lib/i18n";
import { useInstalledState } from "../lib/useInstalledState";

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const installed = useInstalledState();
  const headerRef = React.useRef(null);

  const logoSrc = lang === "pl" ? "/brand/logo-pl.png" : "/brand/logo-en.jpg";

  // Close mobile menu whenever the route changes (defensive — keeps the
  // overlay state from getting stuck after a NavLink/Link click).
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // ESC and any click/tap that lands outside the header close the menu.
  // Using a document-level listener (rather than a backdrop element) means
  // we don't have to fight z-index battles with the chat FAB, tab bar etc.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    // pointerdown fires before click; this also catches taps on touch.
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  // Smooth-scroll to a hash on the home page. If we're not on "/", first navigate there.
  const goToHash = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${hash}`);
      return;
    }
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${hash}`);
    }
  };

  // "Home" link: smooth-scroll to top when already on "/", otherwise SPA-navigate home.
  const goHome = (e) => {
    setOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      if (window.location.hash) {
        window.history.replaceState(null, "", "/");
      }
    }
  };

  const links = [
    { type: "route", to: "/", label: t("nav.home"), key: "home" },
    {
      type: "route",
      to: "/services/driveway-pressure-washing",
      label: t("nav.services"),
      key: "services",
    },
    { type: "hash", hash: "pricing", label: t("nav.pricing"), key: "pricing" },
    { type: "hash", hash: "faq", label: t("nav.faq"), key: "faq" },
    { type: "route", to: "/portal", label: t("nav.portal"), key: "portal" },
  ];

  // Compact, always-visible language switcher used in both desktop and mobile
  // header rows. Keeping it visible at every breakpoint means visitors can
  // toggle PL/EN without opening the hamburger first.
  const langSwitcher = (
    <div
      className="flex items-center rounded-full border border-slate-200 bg-white/70 p-0.5 text-[11px] font-bold"
      data-testid="lang-switcher"
    >
      <button
        onClick={() => setLang("pl")}
        className={`px-2.5 py-1 rounded-full transition ${
          lang === "pl" ? "bg-sky-600 text-white" : "text-slate-600"
        }`}
        data-testid="lang-pl"
        aria-pressed={lang === "pl"}
      >
        PL
      </button>
      <button
        onClick={() => setLang("en")}
        className={`px-2.5 py-1 rounded-full transition ${
          lang === "en" ? "bg-sky-600 text-white" : "text-slate-600"
        }`}
        data-testid="lang-en"
        aria-pressed={lang === "en"}
      >
        EN
      </button>
    </div>
  );

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 glass border-b border-white/40"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 h-20 flex items-center justify-between gap-2">
        <Link
          to="/"
          onClick={goHome}
          className="flex items-center group shrink-0"
          data-testid="logo-home-link"
        >
          <img
            src={logoSrc}
            alt="LocalH2O — Driveway & Bin Cleaning, Gdańsk"
            className="h-12 sm:h-14 lg:h-16 w-auto select-none"
            draggable={false}
            data-testid={`brand-logo-${lang}`}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) =>
            l.type === "hash" ? (
              <button
                key={l.key}
                onClick={() => goToHash(l.hash)}
                className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors"
                data-testid={`nav-link-${l.key}`}
              >
                {l.label}
              </button>
            ) : (
              <NavLink
                key={l.key}
                to={l.to}
                onClick={l.key === "home" ? goHome : undefined}
                className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors"
                data-testid={`nav-link-${l.key}`}
              >
                {l.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {installed && (
            <span
              className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold"
              data-testid="header-installed-badge"
              title={t("install.installed_subtitle")}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {t("install.installed_short_label")}
            </span>
          )}
          {/* Always-visible language switcher (mobile + tablet + desktop). */}
          {langSwitcher}
          <button
            onClick={() => navigate("/book")}
            className="btn-primary hidden sm:inline-flex text-sm"
            data-testid="header-book-now-btn"
          >
            {t("nav.book")}
          </button>
          <button
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
            className="md:hidden absolute inset-x-0 top-full border-t border-slate-200 bg-white/95 backdrop-blur shadow-lg origin-top"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-3">
              {links.map((l) =>
                l.type === "hash" ? (
                  <button
                    key={l.key}
                    onClick={() => goToHash(l.hash)}
                    className="text-base text-slate-700 py-1 text-left"
                    data-testid={`mobile-link-${l.key}`}
                  >
                    {l.label}
                  </button>
                ) : (
                  <Link
                    key={l.key}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="text-base text-slate-700 py-1"
                    data-testid={`mobile-link-${l.key}`}
                  >
                    {l.label}
                  </Link>
                ),
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/book");
                }}
                className="btn-primary mt-2"
                data-testid="mobile-book-now-btn"
              >
                {t("nav.book")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
